import { Injectable, inject, PLATFORM_ID } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { isPlatformServer } from "@angular/common";
import { catchError, map, Observable, of, retry, timeout } from "rxjs";
import { environment } from "../../../environments/env";
import {
  CreateReviewRequest,
  Review,
  ReviewsResponse,
  ApproveReviewRequest,
  CanReviewResponse,
  ReviewSummary,
} from "../interfaces/review.interfaces";

@Injectable({
  providedIn: "root",
})
export class ReviewService {
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly baseUrl = `${environment.apiMagroLabs}/reviews`;

  /**
   * Obtener todas las reseñas
   * GET /api/v1/reviews
   */
  getAllReviews(params?: {
    product_id?: string;
    is_approved?: boolean;
    page?: number;
    per_page?: number;
  }): Observable<ReviewsResponse> {
    let httpParams = new HttpParams();

    if (params?.product_id) {
      // Backend espera `product` (no `product_id`). Ver review.controller.getAllReviews.
      httpParams = httpParams.set("product", params.product_id);
    }
    if (params?.is_approved !== undefined) {
      httpParams = httpParams.set("is_approved", params.is_approved.toString());
    }
    if (params?.page) {
      httpParams = httpParams.set("page", params.page.toString());
    }
    if (params?.per_page) {
      // Backend espera `limit` (no `per_page`) para el tamaño de página.
      httpParams = httpParams.set("limit", params.per_page.toString());
    }

    return this.http.get<ReviewsResponse>(this.baseUrl, { params: httpParams });
  }

  /**
   * Crear una nueva reseña
   * POST /api/v1/reviews
   */
  createReview(reviewData: CreateReviewRequest): Observable<Review> {
    return this.http.post<Review>(this.baseUrl, reviewData);
  }

  /**
   * Obtener una reseña por ID
   * GET /api/v1/reviews/{id}
   */
  getReviewById(id: string): Observable<Review> {
    return this.http.get<Review>(`${this.baseUrl}/${id}`);
  }

  /**
   * Actualizar una reseña
   * PUT /api/v1/reviews/{id}
   */
  updateReview(
    id: string,
    reviewData: Partial<CreateReviewRequest>,
  ): Observable<Review> {
    return this.http.put<Review>(`${this.baseUrl}/${id}`, reviewData);
  }

  /**
   * Eliminar una reseña (soft delete)
   * DELETE /api/v1/reviews/{id}
   */
  deleteReview(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  /**
   * Aprobar o rechazar una reseña (admin)
   * PATCH /api/v1/reviews/{id}/approve
   */
  approveReview(
    id: string,
    approveData: ApproveReviewRequest,
  ): Observable<Review> {
    return this.http.patch<Review>(
      `${this.baseUrl}/${id}/approve`,
      approveData,
    );
  }

  /**
   * Restaurar una reseña eliminada (admin)
   * PATCH /api/v1/reviews/{id}/restore
   */
  restoreReview(id: string): Observable<Review> {
    return this.http.patch<Review>(`${this.baseUrl}/${id}/restore`, {});
  }

  /**
   * Obtener reseñas por producto con soporte de paginación.
   * Forwarda page y per_page al endpoint GET /api/v1/reviews.
   */
  getReviewsByProduct(
    productId: string,
    onlyApproved = true,
    page?: number,
    perPage?: number,
  ): Observable<ReviewsResponse> {
    return this.getAllReviews({
      product_id: productId,
      is_approved: onlyApproved,
      page,
      per_page: perPage,
    });
  }

  /**
   * Verificar si el usuario puede escribir una reseña para un producto
   * GET /api/v1/reviews/can-review/{product_id}
   */
  canUserReviewProduct(productId: string): Observable<CanReviewResponse> {
    return this.http
      .get<{
        status: string;
        data: CanReviewResponse;
      }>(`${this.baseUrl}/can-review/${productId}`)
      .pipe(map((response) => response.data));
  }

  /**
   * Obtener el resumen de calificaciones de un producto.
   * GET /api/v1/reviews/summary/:productId
   *
   * Endpoint público (sin Authorization). Degradación elegante: si el endpoint
   * no existe aún o responde con error o supera los 5 s, emite null y completa
   * sin lanzar excepción. El caller nunca debe recibir un error de este método.
   */
  getSummary(productId: string): Observable<ReviewSummary | null> {
    const onServer = isPlatformServer(this.platformId);
    const request$ = this.http
      .get<{
        status: string;
        data: ReviewSummary;
      }>(`${this.baseUrl}/summary/${productId}`)
      .pipe(map((res) => res.data));

    // Server/prerender: generous timeout + retries so a cold backend still bakes
    // the rating into the static HTML. Browser/SPA nav: short 3s timeout, no retry,
    // so a slow/down backend never blocks navigation (falls back to empty-state fast).
    const resilient$ = onServer
      ? request$.pipe(timeout(20000), retry({ count: 2, delay: 1000 }))
      : request$.pipe(timeout(3000));

    return resilient$.pipe(catchError(() => of(null)));
  }
}
